import React from 'react';
import { theme } from '../theme';
import type { Flag, FlagId } from '../serverTypes';
import { InfoTooltip } from './InfoTooltip';
import { ServedFace } from './ServedFace';
import { ShieldIcon } from './icons';
import { pillStyle, INFO_PILL_BORDER_WIDTH } from './SecondaryAxisPills';

// <purpose>THE flag pill: a recorded signal beside the classification (a danger report, a legal
// reference, an attack, junk, a bounce). Outline shape (white fill, 1.5px border in the flag's hue),
// ONE served word, no glyph, no age (part 7, 2026-10-11: info pills are short and icon-free). The
// hover is the served explanation (the mail's own words that earned the flag — the detail header
// passes it) or NOTHING, never the word again (part 7 round 3, 2026-10-14, operator ruling: an info
// pill hovers a served explanation or nothing). A list row serves flag ids only, so its pill is bare.
// The arrival has one clock, the row-1 age (ArrivalAge) — the flag carries no date.</purpose>
// <constraints>Hues: danger IS the K4 meaning, so it takes the K4 orange token
// (theme.colors.warning, the AA-darkened K4 — never a second literal); legal and attack are the
// brand navy; junk and bounce the unclassified gray. The wording is served (faces family `flag`),
// never spelled here, and it reaches the DOM through ServedFace (final review, part 7, 2026-10-11):
// a cold first load paints a shimmer at the pill's footprint — never the raw id („danger"), never a
// re-flow when the word lands. A flag is a safety signal and is never hidden by its face: a served
// null falls to the raw-key floor (nullAs="key", as StateTag does), never to nothing. The one place
// a flag is not drawn is decided by the CALLER, not here: the implication rule (part 7 fix loop,
// 2026-10-12, services/impliedSignals) — a flag the visible band already implies.
// DOMINANCE_ORDER is the order several flags on one row are drawn in.</constraints>
export const FLAG_HUES: Record<FlagId, string> = {
  danger: theme.colors.warning,
  legal: theme.colors.navy,
  attack: theme.colors.navy,
  junk: theme.statusStrip.unclassified,
  bounce: theme.statusStrip.unclassified,
};

export const FLAG_DOMINANCE_ORDER: readonly FlagId[] = ['danger', 'legal', 'attack', 'junk', 'bounce'];

export const isFlagId = (s: string): s is FlagId => (FLAG_DOMINANCE_ORDER as readonly string[]).includes(s);

/** The served flag ids of a row, known ones only, in dominance order. */
export const orderedFlags = (flags: readonly string[] | null | undefined): FlagId[] =>
  FLAG_DOMINANCE_ORDER.filter(id => flags?.includes(id));

/** The served-quote hover's second state: the cited quote is not in the mail text. One literal, shared by
 *  the header flag hover and the Begründung's per-fact hover. */
export const QUOTE_NOT_FOUND_HOVER = 'Zitat nicht im Text gefunden';

/** The detail header's hover for a flag: the served quote — the mail's own words that earned it — or,
 *  when the quote is not in the text, that fact; no quote ⇒ null (no hover). Same two states as the
 *  Begründung's per-fact hover; a check/header flag with a matched span is a quote too. */
export const flagQuoteHover = (flag: Pick<Flag, 'quote' | 'quoteFound'> | undefined): string | null => {
  if (!flag?.quote) return null;
  return flag.quoteFound === false ? QUOTE_NOT_FOUND_HOVER : `„${flag.quote}“`;
};

// The pill's footprint while the vocabulary loads: a short served word at the row metrics.
const FLAG_PILL_SKELETON_WIDTH = 52;
const FLAG_PILL_SKELETON_HEIGHT = 16;

export const FlagPill: React.FC<{ id: FlagId; hover?: string | null }> = ({ id, hover }) => {
  const hue = FLAG_HUES[id];
  return (
    <ServedFace family="flag" faceKey={id} nullAs="key" width={FLAG_PILL_SKELETON_WIDTH} height={FLAG_PILL_SKELETON_HEIGHT} render={word => {
      const pill = (
        <span
          data-testid="flag-pill"
          data-flag={id}
          style={{
            ...pillStyle('info', 'row'),
            display: 'inline-flex', alignItems: 'center', fontWeight: 600,
            backgroundColor: theme.colors.white, color: hue, border: `${INFO_PILL_BORDER_WIDTH} solid ${hue}`,
          }}
        >
          {word}
        </span>
      );
      return hover ? <InfoTooltip text={hover}>{pill}</InfoTooltip> : pill;
    }} />
  );
};

// <purpose>The flag's SECOND face (detail header, 2026-10-31): the same served word in the same hue
// as FlagPill, bold 12 px with a 12 px shield, no frame — the header's meta line states the flags as
// words while the list row keeps the pill. Same word source (ServedFace, raw-key floor), same hue
// table, same hover (the mail's quote or nothing). Registry: entry `info_pills`, the flag's faces.</purpose>
// <constraints>A word with a hover is a keyboard stop (InfoTooltip makes its anchor focusable), so the
// quote that earned the flag reaches keyboard and screen-reader users too.</constraints>
export const FlagWord: React.FC<{ id: FlagId; hover?: string | null }> = ({ id, hover }) => {
  const hue = FLAG_HUES[id];
  return (
    <ServedFace family="flag" faceKey={id} nullAs="key" width={FLAG_PILL_SKELETON_WIDTH} height={FLAG_PILL_SKELETON_HEIGHT} render={word => {
      const face = (
        <span
          data-testid="flag-word"
          data-flag={id}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 3, flexShrink: 0, whiteSpace: 'nowrap',
            fontSize: 12, fontWeight: 700, color: hue, outline: 'none',
          }}
        >
          <ShieldIcon size={12} color={hue} />
          {word}
        </span>
      );
      return hover ? <InfoTooltip text={hover}>{face}</InfoTooltip> : face;
    }} />
  );
};
